import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, LoadingState, StatePanel } from '@sakhtar/design-system/react';
import type { ApiGetState } from '../../lib/useApiGet.js';
import type { ApiError } from '../../lib/apiClient.js';
import type {
  SetupLegalIdentityInput,
  SetupPersonType,
  SetupPrimaryLocationInput,
} from '../../lib/types/setup.js';
import './print-document.css';

/**
 * Shared chrome for every A4-landscape print document (پیش‌فاکتور/فاکتور/
 * گزارش). Not the design-canvas-only `<doc-page>`/`<image-slot>` scaffolding
 * from the design tool (print-preview UX for the design tool itself) —
 * reimplemented directly with `@page` + a fixed-size container, per this
 * feature's own investigation (see the PR description).
 */
export function PrintPageShell({
  backHref,
  backLabel,
  children,
}: {
  readonly backHref: string;
  readonly backLabel: string;
  readonly children: ReactNode;
}) {
  return (
    <div className="sk-print-doc-screen-frame" dir="rtl">
      <div
        className="sk-print-hide"
        style={{
          width: '297mm',
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Link to={backHref} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="chevronRight" size="sm" />
          {backLabel}
        </Link>
        <button type="button" className="sk-btn sk-btn--primary" onClick={() => window.print()}>
          <Icon name="invoice" size="sm" />
          چاپ
        </button>
      </div>
      <div className="sk-print-doc" dir="rtl">
        {children}
      </div>
    </div>
  );
}

/** Loading/error states shared by every print page's data fetch — same
 * status vocabulary as every other page in this app (`useApiGet`). */
export function PrintLoadState({ error }: { readonly error?: ApiError | undefined }) {
  if (error) {
    return (
      <StatePanel
        variant={
          error.status === 403
            ? 'permission_denied'
            : error.status === 404
              ? 'not_found'
              : 'network_error'
        }
        title={
          error.status === 403
            ? 'دسترسی ندارید'
            : error.status === 404
              ? 'پیدا نشد'
              : 'قابل نمایش نیست'
        }
      />
    );
  }
  return <LoadingState />;
}

export function isReady<T>(
  state: ApiGetState<T>,
): state is Extract<ApiGetState<T>, { status: 'ready' }> {
  return state.status === 'ready';
}

/** Header identity block — tenant's own legal identity (right, RTL-start)
 * beside the document title block a caller renders alongside it. Any field
 * left unset in setup renders "—", never a fabricated placeholder. */
export function TenantIdentityBlock({
  legalIdentity,
  primaryLocation,
  personType,
}: {
  readonly legalIdentity: SetupLegalIdentityInput | null;
  readonly primaryLocation: SetupPrimaryLocationInput | null;
  readonly personType?: SetupPersonType | null;
}) {
  const ownerName =
    legalIdentity?.ownerFirstName || legalIdentity?.ownerLastName
      ? [legalIdentity.ownerFirstName, legalIdentity.ownerLastName].filter(Boolean).join(' ')
      : null;
  const printedName =
    personType === 'individual'
      ? (ownerName ?? legalIdentity?.invoiceDisplayName ?? primaryLocation?.name ?? '—')
      : personType === 'company'
        ? (legalIdentity?.legalName ??
          legalIdentity?.invoiceDisplayName ??
          primaryLocation?.name ??
          '—')
        : (legalIdentity?.invoiceDisplayName ??
          legalIdentity?.legalName ??
          ownerName ??
          primaryLocation?.name ??
          '—');
  const nationalId = personType === 'individual' ? null : (legalIdentity?.nationalId ?? null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 18, fontWeight: 700 }}>{printedName || '—'}</span>
      <span style={{ fontSize: 11, color: '#555' }}>{legalIdentity?.address || '—'}</span>
      <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#555', flexWrap: 'wrap' }}>
        <span className="ltr">{legalIdentity?.phone || '—'}</span>
        <span>شناسهٔ ملی {nationalId || '—'}</span>
        <span>کد اقتصادی {legalIdentity?.economicCode || '—'}</span>
      </div>
    </div>
  );
}
